import React, { useState, useEffect } from 'react';
import { X, Link, Copy, Check, Trash, ArrowsClockwise, ArrowSquareOut, Eye } from '@phosphor-icons/react';
import { createShare, getShare, revokeShare, type ShareInfo } from '../api/client';

interface ShareDialogProps {
  deckId: string;
  onClose: () => void;
}

export default function ShareDialog({ deckId, onClose }: ShareDialogProps) {
  const [share, setShare] = useState<ShareInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getShare(deckId).then(s => { setShare(s); setLoading(false); }).catch(() => setLoading(false));
  }, [deckId]);

  const fullUrl = share ? (share.url.startsWith('http') ? share.url : `${window.location.origin}${share.url}`) : '';

  async function handleCreate() {
    setCreating(true);
    setError('');
    try {
      const s = await createShare(deckId);
      setShare(s);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create share');
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke() {
    try {
      await revokeShare(deckId);
      setShare(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to revoke share');
    }
  }

  function handleCopy() {
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--ds-overlay)', backdropFilter: 'blur(4px)',
    }} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg-surface)', borderRadius: 14,
          width: 420, maxWidth: '90vw', boxShadow: '0 20px 60px var(--ds-shadow-color)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid var(--border)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Link size={16} weight="light" style={{ color: 'var(--brand)' }} />
            <span style={{ fontSize: 'var(--fs-md)', fontWeight: 600, color: 'var(--text-primary)' }}>
              Share Presentation
            </span>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 4,
            color: 'var(--text-muted)', borderRadius: 6,
          }}>
            <X size={16} weight="light" />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20 }}>Loading...</div>
          ) : share ? (
            /* Has active share */
            <div>
              <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginBottom: 12 }}>
                Anyone with this link can view your presentation.
              </p>

              {/* URL bar */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', background: 'var(--bg-surface)',
                border: '1px solid var(--border)', borderRadius: 8,
                marginBottom: 12,
              }}>
                <input
                  readOnly value={fullUrl}
                  style={{
                    flex: 1, background: 'none', border: 'none', outline: 'none',
                    fontSize: 'var(--fs-sm)', color: 'var(--text-primary)', fontFamily: 'monospace',
                  }}
                />
                <button onClick={handleCopy} title="Copy link" style={{
                  background: copied ? 'var(--brand-light)' : 'var(--brand)', color: 'var(--text-on-brand, #fff)',
                  border: 'none', borderRadius: 6, padding: '5px 12px',
                  fontSize: 'var(--fs-sm)', fontWeight: 600, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'inherit',
                  transition: 'background 0.2s',
                }}>
                  {copied ? <Check size={13} weight="light" /> : <Copy size={13} weight="light" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>

              {/* Stats + actions */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span style={{
                  fontSize: 'var(--fs-xs)', color: 'var(--text-muted)',
                  display: 'flex', alignItems: 'center', gap: 4,
                }}>
                  <Eye size={12} weight="light" />
                  {share.view_count} view{share.view_count !== 1 ? 's' : ''}
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => window.open(fullUrl, '_blank')} style={{
                    background: 'none', border: '1px solid var(--border)',
                    borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
                    fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)',
                    display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'inherit',
                  }}>
                    <ArrowSquareOut size={12} weight="light" />
                    Open
                  </button>
                  <button onClick={handleCreate} disabled={creating} style={{
                    background: 'none', border: '1px solid var(--border)',
                    borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
                    fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)',
                    display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'inherit',
                    opacity: creating ? 0.5 : 1,
                  }}>
                    <ArrowsClockwise size={12} weight="light" />
                    Update
                  </button>
                  <button onClick={handleRevoke} style={{
                    background: 'none', border: '1px solid var(--color-danger, var(--ds-danger))',
                    borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
                    fontSize: 'var(--fs-sm)', color: 'var(--color-danger, #ef4444)',
                    display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'inherit',
                  }}>
                    <Trash size={12} weight="light" />
                    Revoke
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* No share yet */
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <p style={{
                fontSize: 'var(--fs-base)', color: 'var(--text-secondary)',
                marginBottom: 16, lineHeight: 1.5,
              }}>
                Create a public link to share your presentation. Anyone with the link can view it without logging in.
              </p>
              <button onClick={handleCreate} disabled={creating} style={{
                padding: '10px 28px', fontSize: 'var(--fs-md)', fontWeight: 600,
                background: 'var(--brand)', color: 'var(--text-on-brand, #fff)', border: 'none', borderRadius: 10,
                cursor: 'pointer', fontFamily: 'inherit',
                display: 'inline-flex', alignItems: 'center', gap: 8,
                opacity: creating ? 0.6 : 1,
              }}>
                <Link size={15} weight="light" />
                {creating ? 'Creating...' : 'Create Share Link'}
              </button>
            </div>
          )}

          {error && (
            <p style={{ color: 'var(--color-danger, #ef4444)', fontSize: 'var(--fs-sm)', marginTop: 12, textAlign: 'center' }}>
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
